/**
 * GA4 Configuration Guide Component
 *
 * In-app guide for store owners to verify GA4 e-commerce tracking is properly configured
 * Shows step-by-step instructions and validates configuration
 */

import { useState, useEffect } from 'react';
import { Card, Alert, Button, Tag, Icon, Spinner, Title, Text } from '@nimbus-ds/components';
import { CheckCircleIcon, CloseIcon, ExclamationCircleIcon, ExternalLinkIcon } from '@nimbus-ds/icons';
import { ga4Service } from '@/api/ga4';

interface GA4ConfigStatus {
  is_configured?: boolean;
  missing_events?: string[];
  has_data?: boolean;
  events_found?: string[];
  message?: string;
  error?: string;
}

interface GA4ConfigurationGuideProps {
  storeId: string;
  onConfigured?: () => void;
}

export function GA4ConfigurationGuide({ storeId, onConfigured }: GA4ConfigurationGuideProps) {
  const [status, setStatus] = useState<GA4ConfigStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateConfiguration = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await ga4Service.testAccess(storeId);
      setStatus(response);

      if (response.is_configured && onConfigured) {
        onConfigured();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to validate GA4 configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    validateConfiguration();
  }, [storeId]);

  const getStatusIcon = () => {
    if (loading) return <Spinner size="small" />;
    if (!status) return <span className="text-gray-500"><Icon source={<ExclamationCircleIcon />} color="currentColor" /></span>;
    if (status.is_configured) return <span className="text-green-500"><Icon source={<CheckCircleIcon />} color="currentColor" /></span>;
    return <span className="text-red-500"><Icon source={<CloseIcon />} color="currentColor" /></span>;
  };

  const getStatusBadge = () => {
    if (loading) return <Tag appearance="neutral">Checking...</Tag>;
    if (!status) return <Tag appearance="neutral">Unknown</Tag>;
    if (status.is_configured) return <Tag appearance="success">Configured</Tag>;
    if (status.has_data) return <Tag appearance="warning">Partial</Tag>;
    return <Tag appearance="danger">Not Configured</Tag>;
  };

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getStatusIcon()}
            <Title as="h3">GA4 E-commerce Tracking</Title>
          </div>
          <div className="shrink-0 whitespace-nowrap">{getStatusBadge()}</div>
        </div>
        <Text>
          Verify that Google Analytics 4 is properly configured to track your store's funnel analytics
        </Text>
      </Card.Header>

      <Card.Body>
        <div className="space-y-6">
          {/* Status Message */}
          {status && (
            <Alert appearance={status.is_configured ? 'neutral' : 'danger'}>{status.message}</Alert>
          )}

          {error && (
            <Alert appearance="danger">{error}</Alert>
          )}

          {/* Configuration Steps */}
          <div className="space-y-4">
            <Title as="h3">Configuration Steps</Title>

            <div className="space-y-3">
              {/* Step 1: Link GA4 Property */}
              <div className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                  1
                </div>
                <div className="flex-1 space-y-2">
                  <Text color="neutral-textHigh">Link GA4 Property</Text>
                  <Text>
                    Go to TiendaNube admin → Configuration → External Codes → Google Analytics 4
                  </Text>
                  <Button
                    appearance="neutral"
                    size="small"
                    onClick={() => window.open('https://ayuda.tiendanube.com/es_ES/123490-google-analytics/como-vincular-google-analytics-4-con-mi-tiendanube', '_blank')}
                  >
                    View Guide <Icon source={<ExternalLinkIcon />} color="currentColor" />
                  </Button>
                </div>
              </div>

              {/* Step 2: Verify Events */}
              <div className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                  2
                </div>
                <div className="flex-1 space-y-2">
                  <Text color="neutral-textHigh">Verify E-commerce Events</Text>
                  <Text>
                    TiendaNube automatically sends these events. Wait 24-48 hours after linking for data to appear.
                  </Text>
                  {status && (
                    <div className="space-y-2">
                      {/* Required Events */}
                      <div className="space-y-1">
                        <Text fontSize="caption">Required Events:</Text>
                        <div className="flex flex-wrap gap-2">
                          {['view_item', 'add_to_cart', 'begin_checkout', 'purchase'].map(event => {
                            const found = status.events_found?.includes(event);
                            return (
                              <Tag key={event} appearance={found ? 'success' : 'danger'}>
                                {found
                                  ? <Icon source={<CheckCircleIcon />} color="currentColor" />
                                  : <Icon source={<CloseIcon />} color="currentColor" />}
                                {event}
                              </Tag>
                            );
                          })}
                        </div>
                      </div>

                      {/* Missing Events Warning */}
                      {status.missing_events && status.missing_events.length > 0 && (
                        <Alert appearance="warning">
                          Missing events: {status.missing_events.join(', ')}. These should be sent automatically by TiendaNube.
                          If you just linked GA4, wait 24-48 hours. If still missing, contact TiendaNube support.
                        </Alert>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Step 3: Enter Property ID */}
              <div className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                  3
                </div>
                <div className="flex-1 space-y-2">
                  <Text color="neutral-textHigh">Enter GA4 Property ID in TN-Xperiment</Text>
                  <Text>
                    Find your GA4 Property ID in Google Analytics → Admin → Property Settings
                  </Text>
                  <Text fontSize="caption">
                    Format: 123456789 (numbers only)
                  </Text>
                </div>
              </div>
            </div>
          </div>

          {/* Revalidate Button */}
          <div className="flex justify-end">
            <Button
              onClick={validateConfiguration}
              disabled={loading}
              appearance="neutral"
            >
              {loading && <Spinner size="small" />}
              Revalidate Configuration
            </Button>
          </div>

          {/* Additional Resources */}
          <div className="pt-4 border-t space-y-2">
            <Title as="h4">Additional Resources</Title>
            <div className="space-y-1">
              <a
                href="https://ayuda.tiendanube.com/google-analytics/que-eventos-mide-de-forma-automatica-la-integracion-con-google-analytics-4"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                TiendaNube GA4 Automatic Events <ExternalLinkIcon />
              </a>
              <a
                href="https://support.google.com/analytics/answer/14434488"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                GA4 E-commerce Events Reference <ExternalLinkIcon />
              </a>
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

export default GA4ConfigurationGuide;
